import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import ExportButton from '../components/ExportButton.jsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];
const PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

function periodLabel(periodType, dateStr) {
  if (!dateStr) return '';
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00Z');
  if (periodType === 'daily') {
    return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' });
  }
  if (periodType === 'monthly') {
    return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });
  }
  return `Week of ${d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' })}`;
}

function isoDay(v) { return v ? String(v).slice(0, 10) : null; }

// Step a period start (YYYY-MM-DD, as the server computes it in UTC) by one period.
function shiftPeriod(periodType, iso, dir) {
  const d = new Date(iso + 'T00:00:00Z');
  if (periodType === 'daily') d.setUTCDate(d.getUTCDate() + dir);
  else if (periodType === 'monthly') d.setUTCMonth(d.getUTCMonth() + dir);
  else d.setUTCDate(d.getUTCDate() + 7 * dir);
  return d.toISOString().slice(0, 10);
}

export default function WeeklyRecap() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  // CR-2398: admins can filter to a specific rep
  const [filterUserId, setFilterUserId] = useState(null);
  const [period, setPeriod] = useState('weekly');

  const current = useQuery({
    queryKey: ['recap', 'current', period],
    queryFn: () => api.get(`/recaps/current?period=${period}`).then(r => r.data.data),
  });

  // Fetch unfiltered list to populate the rep selector; server filters by filterUserId when set
  const list = useQuery({
    queryKey: ['recaps', filterUserId, period],
    queryFn: () => {
      const params = new URLSearchParams({ period });
      if (filterUserId) params.set('user_id', filterUserId);
      return api.get(`/recaps?${params}`).then(r => r.data.data);
    },
  });

  // Derive unique reps from all recaps the current user can see (unfiltered fetch for the dropdown)
  const allList = useQuery({
    queryKey: ['recaps', null],
    queryFn: () => api.get('/recaps').then(r => r.data.data),
    enabled: isAdmin,
  });
  const reps = useMemo(() => {
    if (!isAdmin || !allList.data) return [];
    const seen = new Set();
    const out = [];
    for (const r of allList.data) {
      if (!seen.has(r.user_id)) {
        seen.add(r.user_id);
        out.push({ id: r.user_id, name: r.user_name });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [isAdmin, allList.data]);

  // Which period the page is on: null = the current one. Reps can step back
  // to earlier periods and generate a recap for them.
  const [selectedStart, setSelectedStart] = useState(null);
  const currentStart = isoDay(current.data?.week_start_date);
  const effectiveStart = selectedStart ?? currentStart;
  const atCurrent = !!currentStart && effectiveStart >= currentStart;

  // The logged-in user's own recap for that period (null if none yet).
  const mine = useQuery({
    queryKey: ['recap', 'mine', period, effectiveStart],
    queryFn: () => api.get(`/recaps/mine?period=${period}&date=${effectiveStart}`).then(r => r.data.data),
    enabled: !!effectiveStart,
  });

  // A recap opened from the list that isn't the user's own (admins viewing a rep).
  const [pickedId, setPickedId] = useState(null);
  const activeId = pickedId ?? mine.data?.id ?? null;

  const draftMutation = useMutation({
    mutationFn: () => api.post('/recaps/draft', { period, week_start_date: effectiveStart }).then(r => r.data.data),
    onSuccess: () => {
      setPickedId(null);
      // CR-2401: broad invalidation so RecapEditor re-fetches its ['recap', id] cache entry
      qc.invalidateQueries({ queryKey: ['recap'] });
      qc.invalidateQueries({ queryKey: ['recaps'] });
    },
  });

  function selectPeriod(p) { setPeriod(p); setSelectedStart(null); setPickedId(null); }
  function stepPeriod(dir) {
    if (!effectiveStart) return;
    const next = shiftPeriod(period, effectiveStart, dir);
    setSelectedStart(currentStart && next >= currentStart ? null : next);
    setPickedId(null);
  }
  function openFromList(r) {
    if (r.user_id === user?.id) {
      const start = isoDay(r.week_start_date);
      setSelectedStart(start === currentStart ? null : start);
      setPickedId(null);
    } else {
      setPickedId(r.id);
    }
  }

  // Viewing another rep's recaps — Generate Recap always runs for the logged-in user
  const viewingOtherRep = (isAdmin && filterUserId && filterUserId !== user?.id) || pickedId != null;
  const hasRecap = !!mine.data;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="section-eyebrow">{PERIODS.find(p => p.value === period)?.label}</div>
          <h1 className="text-2xl">Recap</h1>
          <div className="rule-crimson" />
          <p className="text-xs text-muted mt-1">Quick-capture writes here. Generate Recap any time.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex rounded border border-navy-200 overflow-hidden">
            {PERIODS.map(p => (
              <button key={p.value}
                className={`px-3 py-1.5 text-sm ${period === p.value ? 'bg-navy text-white' : 'bg-white text-navy hover:bg-panel'}`}
                onClick={() => selectPeriod(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
          {isAdmin && reps.length > 0 && (
            <select
              className="input text-sm"
              value={filterUserId ?? ''}
              onChange={e => { setFilterUserId(e.target.value ? parseInt(e.target.value, 10) : null); setPickedId(null); }}
            >
              <option value="">All reps</option>
              {reps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          <ExportButton path="/export/recaps.csv" />
          <button
            className="btn-primary"
            disabled={draftMutation.isPending || viewingOtherRep}
            title={viewingOtherRep ? 'Generate Recap runs for your own recap only' : undefined}
            onClick={() => draftMutation.mutate()}
          >
            {draftMutation.isPending ? 'Generating…' : hasRecap ? 'Regenerate Recap' : 'Generate Recap'}
          </button>
        </div>
      </header>

      {effectiveStart && (
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-sm px-2" aria-label="Previous period" onClick={() => stepPeriod(-1)}>‹</button>
          <div className="text-sm font-medium text-navy min-w-[12rem] text-center">
            {periodLabel(period, effectiveStart)}{atCurrent ? ' (current)' : ''}
          </div>
          <button className="btn-ghost text-sm px-2" aria-label="Next period" disabled={atCurrent} onClick={() => stepPeriod(1)}>›</button>
          {!atCurrent && (
            <button className="text-xs text-crimson" onClick={() => { setSelectedStart(null); setPickedId(null); }}>Back to current</button>
          )}
        </div>
      )}
      {draftMutation.isError && (
        <div className="text-xs text-crimson">{draftMutation.error?.response?.data?.error || 'Could not generate the recap. Try again.'}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1 md:col-span-1">
          {list.data?.map(r => (
            <button key={r.id} onClick={() => openFromList(r)}
              className={`card p-3 w-full text-left ${activeId === r.id ? 'ring-2 ring-crimson' : ''}`}>
              <div className="font-medium">{periodLabel(r.period_type, r.week_start_date)}</div>
              {isAdmin && r.user_name && (
                <div className="text-xs text-navy font-medium">{r.user_name}</div>
              )}
              <div className="text-xs text-muted">{r.status}{r.submitted_at ? ` · ${new Date(r.submitted_at).toLocaleDateString()}` : ''}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2">
          {activeId && <RecapEditor id={activeId} />}
          {!activeId && (
            <div className="card p-4 text-muted">
              {mine.isLoading ? 'Loading…' : `No recap for ${periodLabel(period, effectiveStart).replace(/^Week of/, 'the week of')} yet. Generate Recap drafts one from that period's notes.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RecapEditor({ id }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const r = useQuery({
    queryKey: ['recap', id],
    queryFn: () => api.get(`/recaps/${id}`).then(r => r.data.data),
  });
  const [payload, setPayload] = useState(null);
  useEffect(() => { if (r.data) setPayload(r.data.payload); }, [r.data]);
  const save = useMutation({
    mutationFn: (data) => api.put(`/recaps/${id}`, data).then(r => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recap', id] }),
  });

  // Submit -> preview who it'll go to -> explicit Confirm & Send. Two-step
  // by design: this is a real third-party send, not a status flag, so the
  // confirm click IS the per-message approval for the fixed director-
  // routing list (never shows the operator, who's still silently CC'd
  // server-side — see recapDirectorRouting.js).
  const [showConfirm, setShowConfirm] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const recipients = useQuery({
    queryKey: ['recap', id, 'recipients'],
    queryFn: () => api.get(`/recaps/${id}/recipients`).then(r => r.data.data),
    enabled: showConfirm,
  });
  const sendMutation = useMutation({
    mutationFn: () => api.post(`/recaps/${id}/send`).then(r => r.data),
    onSuccess: (data) => {
      setShowConfirm(false);
      setSentTo(data.sentTo || []);
      qc.invalidateQueries({ queryKey: ['recap', id] });
      qc.invalidateQueries({ queryKey: ['recaps'] });
    },
  });

  if (!payload) return <div className="text-muted">Loading…</div>;

  // CR-2399: server already enforces rep scope (sqlClauseOwned for sales role = user_id only).
  // Admins viewing another rep's recap see it read-only (PUT requires user_id match server-side).
  const isOwner = r.data?.user_id === user?.id;

  function patch(p) { setPayload({ ...payload, ...p }); }

  return (
    <div className="card p-4 space-y-4">
      {!isOwner && (
        <div className="text-xs text-muted bg-panel rounded px-3 py-2">
          Viewing {r.data?.payload?.salesperson_name || 'rep'}'s recap — read only.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><label className="label">Week of</label><input className="input" value={payload.week_of || ''} onChange={e => patch({ week_of: e.target.value })} disabled={!isOwner} /></div>
        <div><label className="label">Territories</label><input className="input" value={payload.territories || ''} onChange={e => patch({ territories: e.target.value })} disabled={!isOwner} /></div>
      </div>

      <Section title="Top 3 Priorities">
        <ol className="space-y-1 list-decimal pl-5">
          {(payload.top_3_priorities || ['','','']).map((p, i) => (
            <li key={i}><input className="input" value={p} disabled={!isOwner}
              onChange={e => {
                const arr = [...(payload.top_3_priorities || ['','',''])]; arr[i] = e.target.value;
                // Edited by the rep: no longer an AI slot, so Regenerate keeps it.
                const slots = (payload.ai_priority_slots || []).filter(x => x !== i);
                patch({ top_3_priorities: arr, ai_priority_slots: slots });
              }} /></li>
          ))}
        </ol>
      </Section>

      <ListSection title="Wins" items={payload.wins} fields={['description', 'impact']}
        readOnly={!isOwner} onChange={items => patch({ wins: items })} />
      <ListSection title="Customer Challenges" items={payload.customer_challenges}
        fields={['customer', 'issue', 'action_taken', 'support_needed']}
        readOnly={!isOwner} onChange={items => patch({ customer_challenges: items })} />
      <ListSection title="Market Recap" items={payload.market_recap}
        fields={['observation', 'trend_impact']}
        readOnly={!isOwner} onChange={items => patch({ market_recap: items })} />
      <ListSection title="New Opportunities" items={payload.new_opportunities}
        fields={['customer', 'description', 'estimated_value', 'next_steps']}
        readOnly={!isOwner} onChange={items => patch({ new_opportunities: items })} />
      <ListSection title="High Alerts" items={payload.high_alerts}
        fields={['account', 'issue', 'urgency', 'recommended_action']}
        readOnly={!isOwner} onChange={items => patch({ high_alerts: items })} />

      {sentTo && (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2">
          ✓ Sent to {sentTo.map(p => p.name).join(', ') || 'your director'}.
        </div>
      )}

      {isOwner && (
        <div className="flex justify-end gap-2 pt-2 border-t border-panelDark">
          <button className="btn-ghost" disabled={save.isPending} onClick={() => { save.mutate({ payload }); setSentTo(null); }}>
            Save draft
          </button>
          <button className="btn-primary" disabled={save.isPending}
            onClick={() => {
              setSentTo(null);
              // Save must complete before the confirm panel opens — Confirm &
              // Send reads the recap's payload straight from the DB, so any
              // unsaved edit needs to land first or it'd be silently excluded
              // from what actually gets emailed.
              save.mutate({ payload }, { onSuccess: () => setShowConfirm(true) });
            }}>
            Submit
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="section-eyebrow">Submit Recap</div>
            <h2 className="text-lg font-medium text-navy">Send this recap?</h2>
            {recipients.isLoading && <p className="text-sm text-muted">Loading recipients…</p>}
            {recipients.data && (
              <p className="text-sm text-ink">
                This recap will be sent to:{' '}
                <strong>{recipients.data.cc.map(p => p.name).join(', ') || 'no one — no director mapped for your role'}</strong>
              </p>
            )}
            {sendMutation.isError && (
              <p className="text-xs text-crimson">Failed to send — try again.</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" disabled={sendMutation.isPending} onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button className="btn-primary" disabled={sendMutation.isPending || recipients.isLoading}
                onClick={() => sendMutation.mutate()}>
                {sendMutation.isPending ? 'Sending…' : 'Confirm & Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="section-eyebrow">{title}</div>
      <div className="rule-crimson" />
      <div className="mt-2">{children}</div>
    </section>
  );
}

function ListSection({ title, items = [], fields, onChange, readOnly = false }) {
  return (
    <Section title={title}>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="bg-panel rounded p-2 space-y-1">
            {it.source === 'ai' && (
              <div className="text-[10px] uppercase tracking-wider text-muted">AI draft · replaced when you regenerate unless you edit it</div>
            )}
            {fields.map(f => (
              <div key={f}>
                <label className="text-[11px] uppercase tracking-wider text-muted">{f.replace(/_/g, ' ')}</label>
                <input className="input" value={it[f] || ''} disabled={readOnly}
                  onChange={e => {
                    // Edited by the rep: drop the AI tag so Regenerate keeps it.
                    const next = items.map((x, i) => {
                      if (i !== idx) return x;
                      const { source, ...rest } = x;
                      return { ...rest, [f]: e.target.value };
                    });
                    onChange(next);
                  }} />
              </div>
            ))}
            {!readOnly && (
              <div className="flex justify-end">
                <button className="text-xs text-crimson"
                        onClick={() => onChange(items.filter((_, i) => i !== idx))}>Remove</button>
              </div>
            )}
          </div>
        ))}
        {!readOnly && (
          <button className="btn-ghost text-sm" onClick={() => onChange([...items, Object.fromEntries(fields.map(f => [f, '']))])}>
            + Add
          </button>
        )}
      </div>
    </Section>
  );
}
